import React from "react";
import {
  GridAggregationModel,
  GridColumnMenu,
  isAutogeneratedRow,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  Alert,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Snackbar,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import StripedDataGrid from "../common/StripedDataGrid";
import { createAdminUserColumns } from "./columns";
import AdminUsersToolbar from "./AdminUsersToolbar";
import UserDetailsDrawer from "./UserDetailsDrawer";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import { applySavedColumnOrder } from "../../utils/gridStatePersistence";

type Props = {
  gridStorageKey: string;
  rows: any[];
  loading: boolean;
  total: number;
  onUsersChanged: () => Promise<void>;
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function AdminUsersColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

export default function AdminUsersGrid({
  gridStorageKey,
  rows,
  loading,
  total,
  onUsersChanged,
}: Props) {
  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const { initialState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey
  );

  const [selectedUser, setSelectedUser] = React.useState<any | null>(null);

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const baseColumns = React.useMemo(
    () => createAdminUserColumns(t, setSelectedUser),
    [t]
  );

  const columns = React.useMemo(
    () => applySavedColumnOrder(baseColumns, initialState),
    [baseColumns, initialState]
  );

  const mergedInitialState = React.useMemo(
    () => ({
      ...initialState,
      pagination: {
        paginationModel: {
          pageSize: 25,
          page: 0,
        },
        ...(initialState as any)?.pagination,
      },
    }),
    [initialState]
  );

  const showSuccess = React.useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "success",
    });
  }, []);

  const showError = React.useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  }, []);

  const handleScheduleSave = React.useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  const isRealUserRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (!params.row.id) return false;

    return true;
  }, []);

  const handleRowClick = React.useCallback(
    (params: any) => {
      if (!isRealUserRow(params)) {
        return;
      }

      setSelectedUser(params.row);
    },
    [isRealUserRow]
  );

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={mergedInitialState}
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        disableRowSelectionOnClick
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onPaginationModelChange={handleScheduleSave}
        onSortModelChange={handleScheduleSave}
        onFilterModelChange={handleScheduleSave}
        onColumnOrderChange={handleScheduleSave}
        onColumnWidthChange={handleScheduleSave}
        onColumnVisibilityModelChange={handleScheduleSave}
        onDensityChange={handleScheduleSave}
        onPreferencePanelClose={handleScheduleSave}
        onRowClick={handleRowClick}
        showToolbar
        slots={{
          toolbar: AdminUsersToolbar as any,
          loadingOverlay: LinearProgress as any,
          columnMenu: AdminUsersColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            title: t("Użytkownicy"),
            total,
          } as any,
        }}
        sx={{
          height: "100%",
          border: "none",

          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            fontWeight: 600,
            color: "#374151",
          },

          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f1f5f9",
          },

          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f9fafb",
            cursor: "pointer",
          },

          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
        }}
      />

      <UserDetailsDrawer
        user={selectedUser}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        onUpdated={onUsersChanged}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}